import { useContext } from 'react';
import React, { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import logo from '../../assets/logo.png';
import ProfileSettings from '../Profiles/ProfileSettings';      // ← new

import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Award,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Ticket,
  BarChart3,
  MessageCircle,
  Briefcase,
  QrCode,
  CircleHelp,
  ScrollText,
  RefreshCw
} from 'lucide-react';
import GuidePanel from '../common/GuidePanel';
import teacherGuide from '../../assets/teacher-readme.md?raw';
import TeacherDashboard from "./TeacherDashboard"; // <-- new import

type TeacherLayoutProps = {
  children: React.ReactNode;
};

export default function TeacherLayout({ children }: TeacherLayoutProps) {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const { hasClaim, userProfile } = useContext(UserContext);    // ← pull profile
  const mainRoleUpper = (userProfile?.mainRole || '').toUpperCase();
  const isTeacher = mainRoleUpper.includes('TEACHER');
  const teacherDashboardRef = useRef<{ openJobs: () => void, showDashboard: () => void, showUserManagement: () => void, showCoupons: () => void, showShop: () => void, showHallPasses: () => void, showSummons: () => void, showCouponReissue: () => void } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [showHelpPanel, setShowHelpPanel] = React.useState(false);

  // Auto-navigate to correct dashboard view based on current route
  // This fixes issue where user gets redirected to dashboard instead of intended page after idle period
  React.useEffect(() => {
    if (!teacherDashboardRef.current) return;
    
    const pathname = location.pathname;
    
    // Map routes to dashboard methods
    const routeHandlers: Record<string, () => void> = {
      '/shop': () => teacherDashboardRef.current?.showShop(),
      '/jobs': () => teacherDashboardRef.current?.openJobs(),
      '/user-management': () => teacherDashboardRef.current?.showUserManagement(),
      '/coupons': () => teacherDashboardRef.current?.showCoupons(),
      '/hallpass': () => teacherDashboardRef.current?.showHallPasses(),
      '/reissue-coupons': () => teacherDashboardRef.current?.showCouponReissue(),
      '/create-summons': () => teacherDashboardRef.current?.showSummons(),
      '/dashboard': () => teacherDashboardRef.current?.showDashboard(),
    };
    
    const handler = routeHandlers[pathname];
    if (handler) {
      // Small delay to ensure dashboard ref is fully initialized
      setTimeout(handler, 100);
    }
  }, [location.pathname, teacherDashboardRef]);

  const menuItems = [
    // Show Dashboard for: non-teacher roles OR teachers who can manage student points
    ...((!isTeacher || hasClaim('CanManageStudentPoints')) ? [{
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      onClick: () => {
  // Navigate to dashboard route so router updates state
  navigate('/dashboard');
        teacherDashboardRef.current?.showDashboard();
      }
    }] : []),
    ...(hasClaim("CanManageStore")
      ? [
        {
          label: 'Shop',
          icon: <ShoppingBag className="w-5 h-5" />,
          onClick: () => {
            navigate('/shop');
            teacherDashboardRef.current?.showShop();
          }
        }]
      : []),
    ...(hasClaim("CanManageBadges")
      ? [
        {
          path: '/badges',
          icon: <Award className="w-5 h-5" />,
          label: 'Badges'
        }]
      : []),
    ...(hasClaim("CanManageJobs")
      ? [
        {
          label: 'Jobs',
          icon: <Briefcase className="w-5 h-5" />,
          onClick: () => {
            navigate('/jobs');
            teacherDashboardRef.current?.openJobs();
          }
        }]
      : []),
    ...(hasClaim("CanManageUsers")
      ? [
        {
          label: 'User Management',
          icon: <Users className="w-5 h-5" />,
          onClick: () => {
            navigate('/user-management');
            teacherDashboardRef.current?.showUserManagement();
          }
        }
      ]
      : []),
    ...(import.meta.env.VITE_ACORN_EDITION === 'full'
      ? [
        {
          path: '/teacher/students',
          icon: <Users className="w-5 h-5" />,
          label: 'Students'
        }
      ]
      : []),
    ...(hasClaim("CanManageAcorns")
      ? [
        {
          label: 'Coupons',
          icon: <QrCode className="w-5 h-5" />,
          onClick: () => {
            navigate('/coupons');
            teacherDashboardRef.current?.showCoupons();
          }
        }
      ]
      : []),
    {
      icon: <Ticket className="w-5 h-5" />,
      label: 'Hall Pass',
      onClick: () => {
  navigate('/hallpass');
        teacherDashboardRef.current?.showHallPasses();
      }
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      label: 'Reissue Coupons',
      onClick: () => {
  navigate('/reissue-coupons');
        teacherDashboardRef.current?.showCouponReissue();
      }
    },
    ...(import.meta.env.VITE_ACORN_EDITION === 'full'
      ? [
        {
          icon: <ScrollText className="w-5 h-5" />,
          label: 'Create Summons',
          onClick: () => {
            navigate('/create-summons');
            teacherDashboardRef.current?.showSummons();   // ← use new method
          }
        }]
      : []),
    // Only show Help menu for non-TEACHER roles
    ...(!isTeacher
      ? [
        {
          icon: <CircleHelp className="w-5 h-5" />,
          label: 'Help',
          onClick: () => setShowHelpPanel(true)
        }
      ]
      : []),
    ...(import.meta.env.VITE_ACORN_EDITION === 'full'
      ? [
        {
          path: '/teacher/achievements',
          icon: <Award className="w-5 h-5" />,
          label: 'Achievements'
        },
        {
          path: '/teacher/reports',
          icon: <BarChart3 className="w-5 h-5" />,
          label: 'Reports'
        },
        {
          path: '/teacher/messages',
          icon: <MessageCircle className="w-5 h-5" />,
          label: 'Messages'
        }
      ]
      : [])
  ];

  const handleLogout = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex">
      {/* Sidebar */}
      <div
        className={`bg-white/60 backdrop-blur-md border-r border-indigo-100/50 h-screen sticky top-0 flex flex-col transition-all duration-300 ${isExpanded ? 'w-64' : 'w-20'
          }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-indigo-100/50">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Logo" className={`w-8 h-8 text-indigo-600 ${!isExpanded && 'mx-auto'}`} />
            {isExpanded && <span className="text-xl font-bold text-gray-800">Teacher Portal</span>}
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            {menuItems.map((item: any) =>
              item.onClick ? (
                <button
                  key={item.label}
                  onClick={() => {
                    item.onClick();
                    // ...any additional onClick handling if needed...
                  }}
                  // Removed reference to undefined "showJobs"
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-gray-50 ${!isExpanded && 'justify-center'}`}
                >
                  {item.icon}
                  {isExpanded && <span className="font-medium">{item.label}</span>}
                </button>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-600 hover:bg-gray-50'
                    } ${!isExpanded && 'justify-center'}`}
                >
                  {item.icon}
                  {isExpanded && <span className="font-medium">{item.label}</span>}
                </Link>
              )
            )}
          </div>
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-indigo-100/50 space-y-2">
          {/* Only show Settings for non-TEACHER roles */}
          {!isTeacher && (
            <button
              onClick={() => setShowProfileModal(true)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors w-full ${!isExpanded && 'justify-center'}`}>
              <Settings className="w-5 h-5" />
              {isExpanded && <span className="font-medium">Settings</span>}
            </button>
          )}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors w-full ${!isExpanded && 'justify-center'}`}>
            <LogOut className="w-5 h-5" />
            {isExpanded && <span className="font-medium">Logout</span>}
          </button>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute -right-3 top-20 bg-white rounded-full p-1 border border-indigo-100/50 shadow-sm hover:bg-gray-50 transition-colors"
        >
          {isExpanded ? (
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {React.Children.map(children, (child) =>
          React.isValidElement(child) && child.type === TeacherDashboard
            ? React.cloneElement(child as React.ReactElement<any>, {
              ref: teacherDashboardRef,
              onOpenProfileSettings: () => setShowProfileModal(true) // ← pass down
            })
            : child
        )}
      </div>

      {/* Sliding Profile pane */}
      <aside
        className={`bg-white border-l border-gray-200 h-screen transition-all duration-300 overflow-hidden ${showProfileModal ? 'w-[33%]' : 'w-0'
          }`}
      >
        {showProfileModal && (
          <ProfileSettings
            profile={userProfile}
            onClose={() => setShowProfileModal(false)}
          />
        )}
      </aside>

      {/* Sliding Help pane */}
      <aside
        className={`bg-white border-l border-gray-200 h-screen transition-all duration-300 overflow-hidden ${showHelpPanel ? 'w-[33%]' : 'w-0'
          }`}
      >
        {showHelpPanel && (
          <GuidePanel markdown={teacherGuide} onClose={() => setShowHelpPanel(false)} />
        )}
      </aside>
    </div>
  );
}